/**
 * /es/financiamiento — Rich Spanish financing page mirroring /financing.
 * Includes patient form downloads, payment options, CareCredit, LendingClub,
 * Invisalign financing, insurance overview, virtual tour, and final CTA.
 */
import React from 'react';
import EsSEO from '@/components/seo/EsSEO';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';

const SpanishFinancingPage = () => {
  const { displayNumber, telLink } = useSpanishTrackingNumber();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: 'Financiamiento Dental — Fort Worth Dental',
    description:
      'Opciones de financiamiento dental flexibles, incluyendo CareCredit, planes de pago y aceptación de seguros para implantes dentales, Invisalign y todos los procedimientos dentales.',
    url: `${SITE}/es/financiamiento`,
    inLanguage: 'es-US',
    provider: {
      '@type': 'Dentist',
      name: 'Fort Worth Dental',
      telephone: '(817) 985-3337',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '4620 Citylake Blvd W',
        addressLocality: 'Fort Worth',
        addressRegion: 'TX',
        postalCode: '76132',
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <EsSEO
        title="Financiamiento Dental, CareCredit y Formularios — Fort Worth Dental"
        description="Atención dental accesible en Fort Worth Dental. CareCredit 0% interés, planes de pago flexibles, aceptamos la mayoría de seguros. Implantes e Invisalign al alcance. Llama al (817) 985-3337."
        canonical="/es/financiamiento"
        hreflangEn="/financing"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20" data-testid="es-financing-hero">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Pagos, CareCredit y Formularios para Pacientes</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Hacemos que pagar tu atención dental sea simple y accesible — explicado paso a paso en español.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Patient Form Downloads */}
          <section className="mb-16" data-testid="es-financing-forms">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">Descarga de Formularios para Pacientes</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  En Fort Worth Dental hemos puesto en línea todos los formularios para pacientes nuevos para tu comodidad. En cuestión de minutos puedes descargarlos y completarlos en casa. Al traer los formularios completos a tu primera cita, todo será rápido y sencillo en la recepción.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Si tienes algún problema descargando los formularios, comunícate con nuestra oficina y un miembro de nuestro equipo te ayudará con gusto.
                </p>
              </div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Formularios para Pacientes Nuevos</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Llama a nuestra oficina al{' '}
                  <a href={telLink} className="text-teal-600 font-medium">
                    {displayNumber}
                  </a>{' '}
                  para recibir tus credenciales del portal del paciente, o descarga los formularios a continuación.
                </p>
                <div className="space-y-4">
                  <a
                    href="/forms/FWD-patientform.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-teal-600 text-white text-center px-6 py-3 rounded-lg hover:bg-teal-700 font-semibold"
                  >
                    Descargar Formulario de Información del Paciente
                  </a>
                  <a
                    href="/forms/LANAP-Post-Op.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gray-700 text-white text-center px-6 py-3 rounded-lg hover:bg-gray-800 font-semibold"
                  >
                    Instrucciones Post-Operatorias LANAP
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Payment Options */}
          <section className="mb-16 bg-gray-50 p-12 rounded-lg" data-testid="es-financing-payment-options">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6 text-center">Opciones de Pago en Fort Worth Dental</h2>
            <p className="text-gray-700 text-lg leading-relaxed text-center mb-8 max-w-4xl mx-auto">
              Sabemos que la capacidad de pagar es una preocupación importante para muchos pacientes. Compartimos esa preocupación y ofrecemos varias opciones de pago.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl mb-4 text-teal-600" aria-hidden="true">🏥</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Seguro Dental</h3>
                <p className="text-gray-600">Si tienes seguro dental, presentamos los reclamos como cortesía para nuestros pacientes.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl mb-4 text-teal-600" aria-hidden="true">💳</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Tarjetas de Crédito</h3>
                <p className="text-gray-600">Aceptamos Visa, MasterCard, American Express, Discover y CareCredit.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl mb-4 text-teal-600" aria-hidden="true">📋</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Ofertas Especiales</h3>
                <p className="text-gray-600">Los descuentos no son acumulables. Solo se puede usar una promoción por paciente.</p>
              </div>
            </div>
          </section>

          {/* CareCredit */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">¿Necesitas Financiamiento Dental? CareCredit te ayuda</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Con orgullo ofrecemos CareCredit a nuestros pacientes — un plan conveniente sin pago inicial y con mensualidades bajas para tratamientos dentales de $1,000 a $25,000.
                </p>
                <div className="space-y-4">
                  <a
                    href="https://www.carecredit.com/apply/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-teal-700"
                  >
                    Solicitar CareCredit
                  </a>
                  <a
                    href="https://www.carecredit.com/login/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gray-700 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-800 ml-4"
                  >
                    Hacer un Pago
                  </a>
                </div>
              </div>
              <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center">
                <div className="bg-white px-8 py-6 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold text-green-600">Care</span>
                    <span className="text-3xl font-bold text-blue-900">Credit</span>
                    <sup className="text-blue-900 text-sm">®</sup>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-center">Financiamiento de Salud</div>
                </div>
              </div>
            </div>
          </section>

          {/* LendingClub */}
          <section className="mb-16 bg-teal-50 p-12 rounded-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="flex items-center justify-center">
                <div className="bg-white px-8 py-6 rounded-lg shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">LC</span>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-800">LendingClub</div>
                      <div className="text-sm text-gray-600 font-medium">Soluciones para Pacientes</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">Plan Verdaderamente Sin Intereses</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Nos complace ofrecer planes de pago flexibles a través de LendingClub Patient Solutions.
                </p>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span><span>Tasas fijas bajas y mensualidades bajas</span></li>
                  <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span><span>Planes verdaderamente sin intereses para pacientes</span></li>
                  <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span><span>Sin pagos iniciales</span></li>
                  <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span><span>Sin penalización por pagar antes</span></li>
                </ul>
                <div className="space-y-4">
                  <a
                    href="https://www.lendingclub.com/patientsolutions/app/check-your-rate?clientid=104739"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700"
                  >
                    Verifica tu Tasa
                  </a>
                  <a
                    href="https://www.lendingclub.com/patientsolutions/payment_calculator?inbound_client_id=105056"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gray-700 text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 ml-4"
                  >
                    Calculadora de Pagos
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Invisalign Financing */}
          <section className="mb-16">
            <div className="bg-purple-50 p-12 rounded-lg text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">Financiamiento Especial — Solo para Invisalign®</h2>
              <p className="text-gray-700 text-lg mb-8">
                Opciones de financiamiento flexible diseñadas específicamente para tratamiento de Invisalign.
              </p>
              <a
                href="https://www.lendingpoint.com/merchants/invisalign/location/ALGN_LH_FORT_6199/ALGN_LH_FORT_6199_PON_URL"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Solicitar Financiamiento de Invisalign
              </a>
            </div>
          </section>

          {/* Insurance */}
          <section className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">Entendiendo tu Seguro Dental</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              En Fort Worth Dental entendemos que navegar el seguro dental puede ser complicado. Por eso te brindamos información detallada sobre los planes que aceptamos para que sepas qué esperar en cuanto a cobertura y beneficios durante tu visita.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Aceptamos la mayoría de planes mayores y presentamos los reclamos como cortesía. Esto significa menos trámites para ti y más enfoque en tu cuidado dental. Si no estás seguro de tu cobertura, nuestro equipo te ayudará a verificar tus beneficios antes de tu cita.
            </p>
          </section>

          {/* Virtual Tour */}
          <section className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6 text-center">Tour Virtual de Nuestra Oficina</h2>
            <div className="aspect-video rounded-lg overflow-hidden shadow-xl max-w-4xl mx-auto">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/phPxGtax2LI"
                title="Tour Virtual — Fort Worth Dental"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </section>

          {/* CTA */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center" data-testid="es-financing-cta">
            <h2 className="text-3xl font-bold mb-4">¿Preguntas Sobre Financiamiento o Formularios?</h2>
            <p className="text-xl mb-8">Nuestro equipo bilingüe está aquí para ayudarte a entender tus opciones.</p>
            <a href={telLink} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 inline-block">
              Llamar a {displayNumber}
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SpanishFinancingPage;
